import { useState } from 'react'

import { Box } from '@chakra-ui/react'
import { generatePath, useParams } from 'react-router'
import { LoadingDots } from 'src/components/LoadingDots/LoadingDots'
import { Paths } from 'src/constants/paths'
import { httpStatus } from 'src/constants/statusCodes'
import { NotFound } from 'src/features/misc/components/NotFound/NotFound'
import type { components } from 'src/lib/api-client/schema.gen'
import { PatientReservationScreenV2_ExplanationReservationDocument } from 'src/lib/gql-client'
import { useGqlQuerySWRNoSuspense } from 'src/lib/gql-client/request'

import { useExpReservationScene } from '../../api/expReservationType'
import { ExplanationReservationSignIn } from '../ExplanationReservationSignIn/ExplanationReservationSignIn'

type Param = {
  trialUid: string
  trialPatientUid: string
  explanationReservationUid: string
}

export const PatientReservationScreenV2: React.FC = () => {
  const { trialUid, trialPatientUid, explanationReservationUid } =
    useParams<Param>()

  if (!trialUid || !trialPatientUid || !explanationReservationUid) {
    throw new Error(
      'trialUid, trialPatientUid, and explanationReservationUid is required',
    )
  }

  const [isUnauthorized, setIsUnauthorized] = useState(false)
  const [hasUnexpectedError, setHasUnexpectedError] = useState(false)

  const [scene, setScene] = useState<
    components['schemas']['output.ReservationScene'] | undefined
  >(undefined)

  useExpReservationScene({
    trialUid,
    explanationReservationUid,
    onSuccess: data => {
      setScene(data.scene)
      mutate()
    },
    onError: () => {
      setHasUnexpectedError(true)
    },
  })

  const { mutate } = useGqlQuerySWRNoSuspense(
    PatientReservationScreenV2_ExplanationReservationDocument,
    !scene
      ? null
      : {
          explanationReservationUid,
        },
    {
      errorRetryCount: 0,
      revalidateOnFocus: false,
      onSuccess: ({ explanationReservation: { explanationRevisionUid } }) => {
        setIsUnauthorized(false)
        if (!scene) {
          return
        }
        let nextPath = ''
        if (scene === 'Explanation') {
          nextPath = generatePath(
            Paths.PatientExplanationRoomLobbyWithoutReservation,
            {
              trialUid,
              explanationRevisionUid,
            },
          )
        }
        if (scene === 'VideoCall') {
          nextPath = generatePath(Paths.PatientTrialPatientVideoCall, {
            trialUid,
            trialPatientUid,
          })
        }
        if (!nextPath) {
          return
        }
        window.location.pathname = nextPath
      },
      onError: error => {
        if (
          error.status === httpStatus.Unauthorized ||
          error.status === httpStatus.BadRequest
        ) {
          setIsUnauthorized(true)
          return
        }
        setIsUnauthorized(false)
        setHasUnexpectedError(true)
      },
    },
  )

  if (!scene) {
    return (
      <Box h="100svh" w="100svw">
        <LoadingDots />
      </Box>
    )
  }
  if (isUnauthorized) {
    return (
      <ExplanationReservationSignIn
        trialUid={trialUid}
        trialPatientUid={trialPatientUid}
        onSignIn={mutate}
        explanationReservationUid={explanationReservationUid}
        scene={scene}
      />
    )
  }

  return hasUnexpectedError ? <NotFound /> : null
}
